// App.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import './App.css';

function App() {
  const [showIntro, setShowIntro] = useState(true);
  const [fadeOut, setFadeOut] = useState(false);
  const [tokenData, setTokenData] = useState({
    price: null,
    fdv: null,
    loading: true,
    error: null
  });

  // Use refs for constants that don't change
  const TOKEN_ADDRESS = useRef('0xf0c0CB4bcde8058AAa904cb3DC9A42654f0710c9').current;
  const POOL_ADDRESS = useRef('0x16f4606d9b7ba693dace26197f6efd19daed6cff').current;
  
  // Memoize ABIs since they never change
  const POOL_ABI = useRef([
    'function slot0() external view returns (uint160 sqrtPriceX96, int24 tick, uint16 observationIndex, uint16 observationCardinality, uint16 observationCardinalityNext, uint8 feeProtocol, bool unlocked)',
    'function token0() external view returns (address)',
    'function token1() external view returns (address)',
    'function liquidity() external view returns (uint128)'
  ]).current;

  const ERC20_ABI = useRef([
    'function decimals() external view returns (uint8)',
    'function symbol() external view returns (string)'
  ]).current;

  useEffect(() => {
    // Handle intro timing
    const timer = setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => {
        setShowIntro(false);
      }, 1000);
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Memoize the fetch function
  const fetchTokenData = useCallback(async () => {
    // Don't fetch if intro is still showing
    if (showIntro) return;
    
    try {
      console.log('🔍 Fetching token data...');
      console.log('📊 Using pool address:', POOL_ADDRESS);
      
      const provider = new ethers.providers.JsonRpcProvider('https://mainnet.optimism.io');
      
      const poolContract = new ethers.Contract(POOL_ADDRESS, POOL_ABI, provider);
      
      // Get token0 and token1
      const token0 = await poolContract.token0();
      const token1 = await poolContract.token1();
      
      const isPPToken0 = token0.toLowerCase() === TOKEN_ADDRESS.toLowerCase();
      const isPPToken1 = token1.toLowerCase() === TOKEN_ADDRESS.toLowerCase();
      
      if (!isPPToken0 && !isPPToken1) {
        throw new Error('PP token not found in pool');
      }
      
      // Get decimals
      const token0Contract = new ethers.Contract(token0, ERC20_ABI, provider);
      const token1Contract = new ethers.Contract(token1, ERC20_ABI, provider);
      
      const [decimals0, decimals1] = await Promise.all([
        token0Contract.decimals(),
        token1Contract.decimals()
      ]);
      
      // Get slot0 data
      const slot0 = await poolContract.slot0();
      const sqrtPriceX96 = slot0.sqrtPriceX96;
      
      if (sqrtPriceX96.isZero()) {
        setTokenData({
          price: 0,
          fdv: 0,
          loading: false,
          error: 'No liquidity in pool yet'
        });
        return;
      }
      
      // Calculate price
      const Q96 = ethers.BigNumber.from(2).pow(96);
      
      // Calculate the ratio as floating point numbers
      const sqrtPriceNum = parseFloat(ethers.utils.formatUnits(sqrtPriceX96, 0));
      const Q96Num = parseFloat(ethers.utils.formatUnits(Q96, 0));
      
      let price;
      if (isPPToken0) {
        // PP is token0, USDC is token1: price = USDC per PP
        const sqrtRatio = sqrtPriceNum / Q96Num;
        const rawPrice = Math.pow(sqrtRatio, 2);
        // Adjust for decimals: USDC (6) - PP (18) = -12, so divide by 10^12
        price = rawPrice / Math.pow(10, 12);
      } else {
        // USDC is token0, PP is token1: price = PP per USDC, but we want USDC per PP
        const sqrtRatio = sqrtPriceNum / Q96Num;
        const ppPerUsdc = Math.pow(sqrtRatio, 2);
        // To get USDC per PP, take the reciprocal
        const usdcPerPp = 1 / ppPerUsdc;
        // Adjust for decimals: PP (18) - USDC (6) = 12, so multiply by 10^12
        price = usdcPerPp * Math.pow(10, 12);
      }
      
      const TOTAL_SUPPLY = 2000000;
      const fdv = price * TOTAL_SUPPLY;
      
      setTokenData({
        price: price,
        fdv: fdv,
        loading: false,
        error: null
      });
      
      console.log('✅ Token data fetched successfully');
      
    } catch (error) {
      console.error('❌ Error fetching token data:', error);
      setTokenData({
        price: null,
        fdv: null,
        loading: false,
        error: 'Failed to fetch token data'
      });
    }
  }, [showIntro, TOKEN_ADDRESS, POOL_ADDRESS, POOL_ABI, ERC20_ABI]);

  // Handle initial fetch only (no interval)
  useEffect(() => {
    if (!showIntro) {
      fetchTokenData();
    }
  }, [showIntro, fetchTokenData]);

  if (showIntro) {
    return (
      <div className={`intro-container ${fadeOut ? 'fade-out' : ''}`}>
        <div className="glitch-text">PP</div>
      </div>
    );
  }

  return (
    <div className="homepage">
      <nav className="navbar">
        <div className="nav-content">
          <span className="nav-brand">PESSIMISM</span>
          <span className="nav-symbol">$PP</span>
        </div>
      </nav>

      <main className="main-content">
        <div className="hero-section">
          <h1 className="hero-title">PESSIMISM</h1>
          <p className="hero-subtitle">A token for the hopeless on Optimism</p>
          <div className="contract-address">
            <span>Contract: </span>
            <a 
              href={`https://optimistic.etherscan.io/token/${TOKEN_ADDRESS}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {TOKEN_ADDRESS.slice(0, 6)}...{TOKEN_ADDRESS.slice(-4)}
            </a>
          </div>
        </div>

        <div className="stats-grid">
          <div className="stat-card">
            <h3>Price</h3>
            {tokenData.loading ? (
              <p className="loading">Loading...</p>
            ) : tokenData.error ? (
              <p className="error">{tokenData.error}</p>
            ) : (
              <>
                <p className="stat-value">${tokenData.price?.toFixed(5)}</p>
                <p className="stat-label">USDC/PP</p>
              </>
            )}
          </div>

          <div className="stat-card">
            <h3>FDV</h3>
            {tokenData.loading ? (
              <p className="loading">Loading...</p>
            ) : tokenData.error ? (
              <p className="error">{tokenData.error}</p>
            ) : (
              <>
                <p className="stat-value">${tokenData.fdv?.toFixed(2)}</p>
                <p className="stat-label">Fully Diluted Value</p>
              </>
            )}
          </div>

          <div className="stat-card">
            <h3>Supply</h3>
            <p className="stat-value">2,000,000</p>
            <p className="stat-label">$PP</p>
          </div>

          <div className="stat-card">
            <h3>Network</h3>
            <p className="stat-value">Optimism</p>
            <p className="stat-label">Chain</p>
          </div>
        </div>

        <div className="section">
          <h2>Tokenomics</h2>
          <div className="tokenomics-grid">
            <div className="tokenomics-item">
              <div className="percentage">75%</div>
              <div>Lucky Draws (1.5M PP)</div>
            </div>
            <div className="tokenomics-item">
              <div className="percentage">25%</div>
              <div>Uniswap V3 LP (500k PP)</div>
            </div>
          </div>
          <p className="note">LP will belocked on UNCX indefinitely, once 0.06 ETH is earned through fees.</p>
        </div>

        <div className="section">
          <h2>The Journey</h2>
          <div className="timeline">
            <div className="timeline-item">
              <h3>Idea Inception</h3>
              <a 
                href="https://debank.com/stream/3455638" 
                target="_blank" 
                rel="noopener noreferrer"
                className="timeline-link"
              >
                Read the original idea →
              </a>
            </div>

            <div className="timeline-item">
              <h3>Round 1</h3>
              <a 
                href="https://debank.com/stream/3456663" 
                target="_blank" 
                rel="noopener noreferrer"
                className="timeline-link"
              >
                First distribution →
              </a>
            </div>

            <div className="timeline-item">
              <h3>Round 2</h3>
              <a 
                href="https://debank.com/stream/3457388" 
                target="_blank" 
                rel="noopener noreferrer"
                className="timeline-link"
              >
                Second round →
              </a>
            </div>

            <div className="timeline-item">
              <h3>Round 3</h3>
              <a 
                href="https://debank.com/stream/3458571" 
                target="_blank" 
                rel="noopener noreferrer"
                className="timeline-link"
              >
                Final round →
              </a>
            </div>

            <div className="timeline-item">
              <h3>Bonus Round</h3>
              <a 
                href="https://debank.com/stream/3459550" 
                target="_blank" 
                rel="noopener noreferrer"
                className="timeline-link"
              >
                Surprise distribution →
              </a>
            </div>

            <div className="timeline-item">
              <h3>Update #1</h3>
              <a 
                href="https://debank.com/stream/3459451" 
                target="_blank" 
                rel="noopener noreferrer"
                className="timeline-link"
              >
                Community update →
              </a>
            </div>
          </div>
        </div>

        <div className="section">
          <h2>Links</h2>
          <div className="links-grid">
            <a 
              href={`https://optimistic.etherscan.io/token/${TOKEN_ADDRESS}`}
              target="_blank"
              rel="noopener noreferrer"
              className="link-button"
            >
              Etherscan
            </a>
            <a 
              href={`https://app.uniswap.org/explore/tokens/optimism/${TOKEN_ADDRESS}`}
              target="_blank"
              rel="noopener noreferrer"
              className="link-button"
            >
              Uniswap
            </a>
            
          </div>
        </div>

        <footer className="footer">
          <p className="disclaimer">
            "There will be no plans, no roadmap no website, blah blah blah, don't buy the token etc. 
            Goo goo ga ga, hopefully we can just have a little fun without hurting anyone. 
            Nothing happens on Optimism anyway so why the fuck not."
          </p>
          <p className="creator">- Original Pessimist</p>
        </footer>
      </main>
    </div>
  );
}

export default App;